import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setUser } from '../lib/authSlice';
import { redeemImpersonationLink } from '../lib/impersonation';

/**
 * Landing page for a one-time impersonation link.
 *
 * The code arrives in the URL FRAGMENT (`/impersonate#code=…`), which browsers
 * never send to a server — it stays out of access logs, proxies and Referer
 * headers. This page reads it, strips it from the address bar before doing
 * anything else, and exchanges it for a session.
 *
 * Redemption runs exactly once per mount. React 18's StrictMode double-invokes
 * effects in development, and a second call against a single-use code would
 * "fail" on a link that just worked — hence the ref guard.
 */
export default function Impersonate() {
  const [error, setError] = useState(null);
  const attempted = useRef(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const hash = window.location.hash || '';
    const code = hash.startsWith('#code=') ? decodeURIComponent(hash.slice('#code='.length)) : null;

    // Clear the fragment immediately: a used code is worthless, but there's no
    // reason to leave it sitting in the address bar to be copied or shared.
    window.history.replaceState(null, '', window.location.pathname);

    if (!code) {
      setError('This link is missing its code. Ask for a fresh one.');
      return;
    }

    (async () => {
      try {
        const target = await redeemImpersonationLink(code);
        dispatch(setUser(target));
        navigate(target.role === 'industry' ? '/industry/' : '/');
      } catch (e) {
        setError(e.message || 'This link is no longer valid. Ask for a fresh one.');
      }
    })();
  }, [dispatch, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-6">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow">
        {error ? (
          <>
            <h1 className="text-lg font-medium text-gray-900">Link not valid</h1>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <p className="mt-4 text-xs text-gray-500">
              Impersonation links work once, and only for a few minutes after they're created.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-medium text-gray-900">Opening session…</h1>
            <p className="mt-2 text-sm text-gray-600">One moment.</p>
          </>
        )}
      </div>
    </div>
  );
}
